/**
 * Shared table UI specs – single source of truth for table layout and density.
 * Use these in Table, TableCell, TableRow, TableContainer, and in-cell components
 * (Chip, IconButton) so all tables have consistent column height, padding, and typography.
 *
 * Import and spread into sx, e.g.:
 *   <TableCell sx={{ ...TABLE_STYLES.cell }} />
 * Override only when a specific table needs to differ.
 */

import * as React from 'react';
import { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SEARCE_COLORS } from '../theme';

/**
 * MUI `TableSortLabel` still mounts the default arrow when `active` is true, even with
 * `hideSortIcon` — see `hideSortIcon && !active ? null : icon` in MUI source. Use this as
 * `IconComponent` so active columns never show a chevron.
 */
export const tableSortLabelEmptyIcon = React.forwardRef<HTMLSpanElement, { className?: string }>(
  function TableSortLabelEmptyIcon(_props, ref) {
    return React.createElement('span', {
      ref,
      'aria-hidden': true,
      style: { display: 'none', width: 0, height: 0, margin: 0, padding: 0, fontSize: 0 },
    });
  }
);

/** Body cell: padding, font, line-height */
export const TABLE_STYLES = {
  /** Body TableCell – compact row height */
  cell: {
    fontSize: '0.75rem',
    py: 0.25,
    px: 1.5,
    lineHeight: 1.25,
  } as SxProps<Theme>,

  /** Header TableCell – slightly more vertical padding than body */
  cellHeader: {
    fontSize: '0.75rem',
    py: 0.5,
    px: 1.5,
    lineHeight: 1.25,
  } as SxProps<Theme>,

  /** Chip inside a table cell – keep rows compact */
  chipInCell: {
    height: '20px',
    fontSize: '0.7rem',
  } as SxProps<Theme>,

  /** IconButton inside a table cell */
  iconButtonInCell: {
    padding: '2px',
  } as SxProps<Theme>,

  /** Table: use with <Table size="small" />; size is a prop, not sx */
  tableSize: 'small' as const,

  /** TableContainer base – border, radius; add maxHeight, overflow per use */
  tableContainer: {
    borderRadius: 2,
    border: `1px solid ${SEARCE_COLORS.border}`,
    overflowX: 'auto' as const,
  } as SxProps<Theme>,

  /** TableRow hover – use when you want the default table row hover */
  tableRowHover: {
    '&:hover': {
      backgroundColor: alpha(SEARCE_COLORS.primary, 0.05),
    },
  } as SxProps<Theme>,

  /** Empty-state cell (e.g. "No data") – extra vertical padding */
  cellEmpty: {
    py: 4,
    color: SEARCE_COLORS.textSecondary,
    fontStyle: 'italic',
  } as SxProps<Theme>,

  /** Clear top padding above title / headerAction (e.g. clear filters + toggle columns). */
  tableToolbarTop: {
    pt: 2,
  } as SxProps<Theme>,

  /** Dense variant of `tableToolbarTop` */
  tableToolbarTopDense: {
    pt: 1.5,
  } as SxProps<Theme>,

  /**
   * Spread onto MUI `TableSortLabel`: sort toggles on header click without a visible chevron
   * (matches tables that use plain clickable headers).
   */
  tableSortLabelProps: {
    hideSortIcon: true,
    IconComponent: tableSortLabelEmptyIcon,
  } as const,

  /**
   * `sx` for `TableSortLabel` on primary (blue) headers: white text, no MUI active underline,
   * icon slot fully suppressed (some themes still reserve space or tint the icon).
   */
  primaryHeaderTableSortLabelSx: {
    color: `${SEARCE_COLORS.white} !important`,
    textDecoration: 'none',
    '&:hover': {
      color: `${SEARCE_COLORS.white} !important`,
    },
    '&.Mui-active': {
      color: `${SEARCE_COLORS.white} !important`,
      textDecoration: 'none',
      '& .MuiTableSortLabel-icon': {
        display: 'none !important',
        opacity: '0 !important',
        width: '0 !important',
        minWidth: 0,
        marginLeft: 0,
        marginRight: 0,
        color: 'transparent !important',
      },
    },
    '& .MuiTableSortLabel-icon': {
      display: 'none !important',
      opacity: '0 !important',
      width: '0 !important',
      minWidth: 0,
      marginLeft: 0,
      marginRight: 0,
      color: 'transparent !important',
    },
  } as SxProps<Theme>,
} as const;

export default TABLE_STYLES;
